import { supabase } from '../lib/supabase';
import { wikipediaService } from './wikipediaService';
import { applyCountryFilter, getStoredLocalePrefs } from '../lib/countryQuery';
import { INTERNATIONAL_CODE } from '../data/countries';

// Local session cache for enriched data to avoid re-fetching
const enrichmentCache = new Map();
const ENRICHMENT_TTL = 15 * 60 * 1000; // 15 minutes

export const userService = {
  /**
   * Check if an avatar URL is a generic placeholder (Unsplash stock photo).
   */
  _isPlaceholderImage(url) {
    if (!url) return true;
    return (
      url.includes('unsplash.com') ||
      url.includes('placeholder') ||
      url.includes('no_image') ||
      url.includes('via.placeholder') ||
      url.includes('picsum.photos')
    );
  },

  /**
   * Enrich a single individual's data with Wikipedia info.
   * Only fetches from Wikipedia if the avatar is a placeholder.
   */
  async _enrichWithWikipedia(profile) {
    const name = profile.full_name || profile.name;
    if (!name) return profile;

    // Check session cache
    const cached = enrichmentCache.get(name);
    if (cached && Date.now() - cached.ts < ENRICHMENT_TTL) {
      return { ...profile, ...cached.data };
    }

    try {
      const wikiData = await wikipediaService.getIndividualInfo(name);
      if (!wikiData) return profile;

      const enrichment = {};

      // Only update image if current one is a placeholder
      if (this._isPlaceholderImage(profile.avatar_url)) {
        if (wikiData.imageUrl) {
          enrichment.avatar_url = wikiData.imageUrl;
          enrichment._wikiImageUrl = wikiData.imageUrl;
        }
      }

      // Enrich with Wikipedia data
      if (wikiData.netWorth) {
        enrichment._wikiNetWorth = wikiData.netWorth;
      }
      if (wikiData.bio) {
        enrichment._wikiBio = wikiData.bio;
      }
      if (wikiData.position) {
        enrichment._wikiPosition = wikiData.position;
      }
      if (wikiData.companies && wikiData.companies.length > 0) {
        enrichment._wikiCompanies = wikiData.companies;
      }
      if (wikiData.politicalLeaning) {
        enrichment._wikiPoliticalLeaning = wikiData.politicalLeaning;
      }
      if (wikiData.education) {
        enrichment._wikiEducation = wikiData.education;
      }
      if (wikiData.location) {
        enrichment._wikiLocation = wikiData.location;
      }
      if (wikiData.wikipediaUrl) {
        enrichment._wikipediaUrl = wikiData.wikipediaUrl;
      }

      enrichmentCache.set(name, { data: enrichment, ts: Date.now() });
      return { ...profile, ...enrichment };
    } catch (error) {
      console.warn(`Wikipedia enrichment failed for ${name}:`, error.message);
      return profile;
    }
  },

  /**
   * Calculate display-ready net worth.
   * Uses Wikipedia data if available, otherwise estimates.
   */
  _getNetWorth(profile) {
    // Prefer Wikipedia-sourced net worth
    if (profile._wikiNetWorth) {
      return profile._wikiNetWorth;
    }
    // Fallback: estimate based on profile metrics
    const baseWorth = 1_000_000_000;
    const companiesOwned = (profile.companies_owned_count || 0) * 500_000_000;
    const investments = (profile.companies_invested_count || 0) * 50_000_000;
    const influence = Math.abs(profile.political_score || 0) * 10_000_000;
    return baseWorth + companiesOwned + investments + influence;
  },

  /**
   * Transform a raw DB profile into card-ready format.
   */
  _transformToCardFormat(profile) {
    // Aggregate PAC data
    const conservativePacs = profile.pac_relationships?.filter(
      pac => {
        const affiliation = (pac.political_affiliation || '').toLowerCase();
        return affiliation.includes('republican') || affiliation.includes('conservative');
      }
    )?.length || 0;

    const liberalPacs = profile.pac_relationships?.filter(
      pac => {
        const affiliation = (pac.political_affiliation || '').toLowerCase();
        return affiliation.includes('democrat') || affiliation.includes('liberal');
      }
    )?.length || 0;

    const totalPacAmount = profile.pac_relationships?.reduce(
      (sum, pac) => sum + (pac.contribution_amount || 0), 0
    ) || 0;

    // Get PAC names for display
    const pacNames = profile.pac_relationships
      ?.map(pac => pac.pac_name)
      ?.filter(Boolean)
      ?.slice(0, 5) || [];

    // Get organization names
    const charityOrgs = profile.organizational_associations
      ?.filter(org => org.organization_type === 'charity' || org.organization_type === 'nonprofit')
      ?.map(org => org.organization_name) || [];

    const thinkTankOrgs = profile.organizational_associations
      ?.filter(org => org.organization_type === 'think_tank')
      ?.map(org => org.organization_name) || [];

    // Determine political color
    const leaning = profile._wikiPoliticalLeaning || profile.political_leaning || 'centrist';
    let politicalColor = '#8B5CF6'; // purple = centrist
    if (leaning === 'conservative') politicalColor = '#DC2626'; // red
    if (leaning === 'liberal') politicalColor = '#2563EB'; // blue

    // Get best available image
    const image = profile._wikiImageUrl || profile.avatar_url || null;

    // Get best bio / position
    const position = profile._wikiPosition || profile.bio || 'Business Executive';
    const bio = profile._wikiBio || profile.bio || 
      `${profile.full_name} is a prominent business leader with significant influence across multiple sectors.`;

    return {
      id: profile.id,
      type: 'individual',
      name: profile.full_name,
      title: position,
      bio: bio,
      
      // Image
      image: image,
      imageAlt: `Photo of ${profile.full_name}`,
      hasRealImage: !this._isPlaceholderImage(image),

      // Financial
      netWorth: this._getNetWorth(profile),
      salary: null, // Not available from public sources at this level

      // Companies (detailed objects for logos & linking)
      companiesOwned: profile._wikiCompanies || [],
      companiesOwnedCount: profile.companies_owned_count || 0,
      companiesInvestedCount: profile.companies_invested_count || 0,
      primaryCompany: profile._wikiCompanies?.[0] || 'Various Companies',

      // Linked companies from executives table (owned/led)
      executiveCompanies: (profile.executives || [])
        .filter(exec => exec.company)
        .map(exec => ({
          id: exec.company.id,
          name: exec.company.name,
          website: exec.company.website,
          logoUrl: exec.company.logo_url,
          stockSymbol: exec.company.stock_symbol,
          industry: exec.company.industry,
          position: exec.position,
          isCurrent: exec.is_current,
          equityPercentage: parseFloat(exec.equity_percentage || 0),
        })),

      // Linked companies from investments table
      investmentCompanies: (profile.investments || [])
        .filter(inv => inv.company && inv.is_active)
        .map(inv => ({
          id: inv.company.id,
          name: inv.company.name,
          website: inv.company.website,
          logoUrl: inv.company.logo_url,
          stockSymbol: inv.company.stock_symbol,
          industry: inv.company.industry,
          investmentAmount: inv.investment_amount,
          equityPercentage: parseFloat(inv.equity_percentage || 0),
          investmentType: inv.investment_type,
        })),

      // Charities & Think Tanks (detailed)
      charitiesSupportedCount: profile.charities_supported_count || 0,
      charityNames: charityOrgs,
      charityDetails: (profile.organizational_associations || [])
        .filter(org => org.organization_type === 'charity' || org.organization_type === 'nonprofit')
        .map(org => ({
          id: org.id,
          name: org.organization_name,
          type: org.organization_type,
          amountDonated: org.amount_donated,
          relationship: org.relationship_type,
        })),
      thinkTanksFundedCount: profile.think_tanks_funded_count || 0,
      thinkTankNames: thinkTankOrgs,
      thinkTankDetails: (profile.organizational_associations || [])
        .filter(org => org.organization_type === 'think_tank')
        .map(org => ({
          id: org.id,
          name: org.organization_name,
          amountDonated: org.amount_donated,
          relationship: org.relationship_type,
        })),

      // PACs & Political (detailed)
      pacAssociationsCount: profile.pac_associations_count || 0,
      pacNames: pacNames,
      pacDetails: (profile.pac_relationships || []).map(pac => ({
        id: pac.id,
        name: pac.pac_name,
        affiliation: pac.political_affiliation,
        amount: pac.contribution_amount,
        relationship: pac.relationship_type,
      })),
      politicalContributions: {
        conservativePacs,
        liberalPacs,
        totalAmount: totalPacAmount,
        totalFormatted: `$${totalPacAmount.toLocaleString()}`,
      },

      // Political leaning
      politicalLeaning: leaning,
      politicalColor: politicalColor,
      politicalScore: profile.political_score || 0,
      conservativeOrgsCount: profile.conservative_orgs_count || 0,
      liberalOrgsCount: profile.liberal_orgs_count || 0,

      // Meta
      location: profile._wikiLocation || profile.location,
      education: profile._wikiEducation || null,
      wikipediaUrl: profile._wikipediaUrl || null,
      createdAt: profile.created_at,
      updatedAt: profile.updated_at,
    };
  },

  // ───────────────────────────────────────────────
  // Public API methods
  // ───────────────────────────────────────────────

  /**
   * Get random prominent individuals with Wikipedia enrichment.
   * This is the main method used by the homepage.
   */
  async getRandomProminentIndividuals(count = 10, options = {}) {
    try {
      if (!supabase) {
        console.error('❌ SUPABASE CLIENT NOT INITIALIZED');
        return [];
      }

      const prefs = options.countryCode ? options : getStoredLocalePrefs();
      const countryCode = options.countryCode || prefs.countryCode;
      const includeInternational = options.includeInternational ?? prefs.includeInternational ?? true;

      // Get total count (with country filter)
      let countQuery = supabase.from('user_profiles').select('*', { count: 'exact', head: true });
      if (countryCode && countryCode !== INTERNATIONAL_CODE) {
        countQuery = applyCountryFilter(countQuery, countryCode, { table: 'user_profiles', includeInternational });
      }
      const { count: totalCount, error: countError } = await countQuery;

      if (countError) {
        console.error('❌ COUNT ERROR:', countError);
        return [];
      }

      if (!totalCount || totalCount === 0) {
        console.warn('⚠️ NO INDIVIDUALS in user_profiles table');
        return [];
      }

      // Random offset
      const maxOffset = Math.max(0, totalCount - count);
      const randomOffset = Math.floor(Math.random() * (maxOffset + 1));

      // Fetch profiles with relationships, executives, and investments
      let query = supabase
        .from('user_profiles')
        .select(`
          *,
          pac_relationships(
            id,
            pac_name,
            political_affiliation,
            contribution_amount,
            relationship_type
          ),
          organizational_associations(
            id,
            organization_name,
            organization_type,
            amount_donated,
            relationship_type
          ),
          executives(
            id,
            position,
            is_current,
            equity_percentage,
            company:companies(
              id, name, website, logo_url, stock_symbol, industry
            )
          ),
          investments(
            id,
            investment_amount,
            equity_percentage,
            investment_type,
            is_active,
            company:companies(
              id, name, website, logo_url, stock_symbol, industry
            )
          )
        `)
        .order('political_score', { ascending: false });

      if (countryCode && countryCode !== INTERNATIONAL_CODE) {
        query = applyCountryFilter(query, countryCode, { table: 'user_profiles', includeInternational });
      }

      const { data, error } = await query.range(randomOffset, randomOffset + count - 1);

      if (error) {
        console.error('❌ QUERY ERROR:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.warn('⚠️ No profiles returned from query');
        return [];
      }

      console.log(`📊 Fetched ${data.length} profiles from Supabase, enriching with Wikipedia...`);

      // Enrich all profiles with Wikipedia data in parallel (batched)
      const enrichedProfiles = [];
      for (let i = 0; i < data.length; i += 3) {
        const batch = data.slice(i, i + 3);
        const enriched = await Promise.all(
          batch.map(profile => this._enrichWithWikipedia(profile))
        );
        enrichedProfiles.push(...enriched);
        
        // Small delay between batches to avoid rate limits
        if (i + 3 < data.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Transform to card format
      const cards = enrichedProfiles.map(profile => this._transformToCardFormat(profile));

      console.log(`✅ ${cards.length} enriched individual cards ready`);
      return cards;
    } catch (error) {
      console.error('❌ getRandomProminentIndividuals failed:', error);
      return [];
    }
  },

  /**
   * Get prominent individuals (paginated, non-random).
   */
  async getProminentIndividuals(limit = 20, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('is_active', true)
        .order('political_score', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      return data?.map(profile => this._transformToCardFormat(profile)) || [];
    } catch (error) {
      console.error('Error fetching prominent individuals:', error);
      return [];
    }
  },

  /**
   * Get specific user profile by ID (basic).
   */
  async getProfileById(id) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          organizational_associations(
            id, organization_name, organization_type, amount_donated,
            relationship_type, start_date, end_date, is_active, source_url
          ),
          pac_relationships(
            id, pac_name, political_affiliation, contribution_amount,
            contribution_date, relationship_type, source_url, is_active
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Enrich with Wikipedia
      const enriched = await this._enrichWithWikipedia(data);

      return {
        id: enriched.id,
        name: enriched.full_name,
        fullName: enriched.full_name,
        email: enriched.email,
        website: enriched.website,
        location: enriched._wikiLocation || enriched.location,
        bio: enriched._wikiBio || enriched.bio,
        avatarUrl: enriched._wikiImageUrl || enriched.avatar_url,
        role: enriched.role,
        isActive: enriched.is_active,
        netWorth: this._getNetWorth(enriched),
        education: enriched._wikiEducation,
        wikipediaUrl: enriched._wikipediaUrl,
        companiesInvestedCount: enriched.companies_invested_count || 0,
        companiesOwnedCount: enriched.companies_owned_count || 0,
        charitiesSupportedCount: enriched.charities_supported_count || 0,
        thinkTanksFundedCount: enriched.think_tanks_funded_count || 0,
        pacAssociationsCount: enriched.pac_associations_count || 0,
        conservativeOrgsCount: enriched.conservative_orgs_count || 0,
        liberalOrgsCount: enriched.liberal_orgs_count || 0,
        politicalScore: enriched.political_score || 0,
        politicalLeaning: enriched._wikiPoliticalLeaning || enriched.political_leaning,
        organizationalAssociations: enriched.organizational_associations?.map(org => ({
          id: org.id,
          organizationName: org.organization_name,
          organizationType: org.organization_type,
          amountDonated: org.amount_donated,
          relationshipType: org.relationship_type,
          startDate: org.start_date,
          endDate: org.end_date,
          isActive: org.is_active,
          sourceUrl: org.source_url,
        })),
        pacRelationships: enriched.pac_relationships?.map(pac => ({
          id: pac.id,
          pacName: pac.pac_name,
          politicalAffiliation: pac.political_affiliation,
          contributionAmount: pac.contribution_amount,
          contributionDate: pac.contribution_date,
          relationshipType: pac.relationship_type,
          sourceUrl: pac.source_url,
          isActive: pac.is_active,
        })),
        createdAt: enriched.created_at,
        updatedAt: enriched.updated_at,
      };
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Get FULL individual profile with all relationships (for profile page).
   * Includes executive roles with company details, investments with company details,
   * PAC relationships, organizational associations, and Wikipedia enrichment.
   */
  async getFullProfileById(id) {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          organizational_associations(
            id, organization_name, organization_type, amount_donated,
            relationship_type, start_date, end_date, is_active, source_url
          ),
          pac_relationships(
            id, pac_name, political_affiliation, contribution_amount,
            contribution_date, relationship_type, source_url, is_active
          ),
          executives(
            id, position, is_current, start_date, equity_percentage,
            company:companies(
              id, name, website, logo_url, stock_symbol, industry,
              headquarters, founded_year, employees, revenue, description, country_of_origin
            )
          ),
          investments(
            id, investment_amount, equity_percentage, investment_type,
            is_active, investment_date,
            company:companies(
              id, name, website, logo_url, stock_symbol, industry,
              headquarters, founded_year, employees, revenue, description, country_of_origin
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      // Enrich with Wikipedia
      const enriched = await this._enrichWithWikipedia(data);

      const name = enriched.full_name || 'Unknown';
      const position = enriched._wikiPosition || enriched.bio || 'Business Executive';
      const bio = enriched._wikiBio || enriched.bio ||
        `${name} is a prominent figure in business and finance.`;
      const image = enriched._wikiImageUrl || enriched.avatar_url;

      // Transform executive roles into companiesOwned
      const executiveCompanies = (enriched.executives || [])
        .filter(exec => exec.company)
        .map(exec => ({
          id: exec.company.id,
          name: exec.company.name,
          industry: exec.company.industry,
          logo: exec.company.logo_url,
          logoAlt: `${exec.company.name} logo`,
          website: exec.company.website,
          stockSymbol: exec.company.stock_symbol,
          headquarters: exec.company.headquarters,
          foundedYear: exec.company.founded_year,
          employees: exec.company.employees,
          revenue: exec.company.revenue,
          description: exec.company.description,
          relationshipType: 'executive',
          positionTitle: exec.position,
          isCurrent: exec.is_current,
          ownershipPercentage: parseFloat(exec.equity_percentage || 0),
          startDate: exec.start_date,
        }));

      // Transform investments into investmentCompanies
      const investmentCompanies = (enriched.investments || [])
        .filter(inv => inv.company)
        .map(inv => ({
          id: inv.company.id,
          name: inv.company.name,
          industry: inv.company.industry,
          logo: inv.company.logo_url,
          logoAlt: `${inv.company.name} logo`,
          website: inv.company.website,
          stockSymbol: inv.company.stock_symbol,
          headquarters: inv.company.headquarters,
          foundedYear: inv.company.founded_year,
          employees: inv.company.employees,
          revenue: inv.company.revenue,
          description: inv.company.description,
          relationshipType: 'investor',
          investmentAmount: inv.investment_amount,
          investmentAmountFormatted: inv.investment_amount
            ? `$${(inv.investment_amount / 1_000_000).toFixed(1)}M`
            : null,
          equityPercentage: parseFloat(inv.equity_percentage || 0),
          investmentType: inv.investment_type,
          isActive: inv.is_active,
          investmentDate: inv.investment_date,
        }));

      // Charities & non-profits
      const charities = (enriched.organizational_associations || [])
        .filter(org => org.organization_type === 'charity' || org.organization_type === 'nonprofit')
        .map(org => ({
          id: org.id,
          name: org.organization_name,
          type: org.organization_type,
          amountDonated: org.amount_donated,
          amountFormatted: org.amount_donated
            ? `$${(org.amount_donated / 1_000_000).toFixed(1)}M`
            : 'Undisclosed',
          relationship: org.relationship_type,
          startDate: org.start_date,
          endDate: org.end_date,
          isActive: org.is_active,
          sourceUrl: org.source_url,
        }));

      // Think tanks
      const thinkTanks = (enriched.organizational_associations || [])
        .filter(org => org.organization_type === 'think_tank')
        .map(org => ({
          id: org.id,
          name: org.organization_name,
          type: org.organization_type,
          amountDonated: org.amount_donated,
          amountFormatted: org.amount_donated
            ? `$${(org.amount_donated / 1_000_000).toFixed(1)}M`
            : 'Undisclosed',
          relationship: org.relationship_type,
          startDate: org.start_date,
          isActive: org.is_active,
          sourceUrl: org.source_url,
        }));

      // PACs
      const pacs = (enriched.pac_relationships || []).map(pac => ({
        id: pac.id,
        name: pac.pac_name,
        affiliation: pac.political_affiliation,
        amount: pac.contribution_amount,
        amountFormatted: pac.contribution_amount
          ? `$${pac.contribution_amount.toLocaleString()}`
          : 'Undisclosed',
        date: pac.contribution_date,
        relationship: pac.relationship_type,
        sourceUrl: pac.source_url,
        isActive: pac.is_active,
      }));

      // Political leaning
      const leaning = enriched._wikiPoliticalLeaning || enriched.political_leaning || 'centrist';

      // Build timeline from executive and investment dates
      const timelineEvents = [];
      executiveCompanies.forEach(exec => {
        if (exec.startDate) {
          timelineEvents.push({
            id: `exec-${exec.id}`,
            type: exec.positionTitle?.toLowerCase().includes('founder') ? 'founding' : 'appointment',
            title: `${exec.positionTitle} at ${exec.name}`,
            description: `Became ${exec.positionTitle} at ${exec.name}${exec.industry ? ` (${exec.industry})` : ''}`,
            date: exec.startDate,
            relatedCompanies: [exec.name],
          });
        }
      });
      investmentCompanies.forEach(inv => {
        if (inv.investmentDate) {
          timelineEvents.push({
            id: `inv-${inv.id}`,
            type: 'investment',
            title: `Invested in ${inv.name}`,
            description: `${inv.investmentAmountFormatted || 'Undisclosed amount'} investment in ${inv.name}${inv.investmentType ? ` (${inv.investmentType})` : ''}`,
            date: inv.investmentDate,
            relatedCompanies: [inv.name],
          });
        }
      });
      // Sort timeline by date descending
      timelineEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

      return {
        // Core profile data
        id: enriched.id,
        name: name,
        fullName: name,
        currentPosition: position,
        bio: bio,
        image: image,
        imageAlt: `Photo of ${name}`,
        hasRealImage: !this._isPlaceholderImage(image),
        
        // Tags
        tags: this._generateTags(enriched, executiveCompanies, investmentCompanies),

        // Stats
        stats: {
          companiesInvestedCount: investmentCompanies.length || enriched.companies_invested_count || 0,
          companiesOwnedCount: executiveCompanies.length || enriched.companies_owned_count || 0,
          charitiesSupportedCount: charities.length || enriched.charities_supported_count || 0,
          thinkTanksFundedCount: thinkTanks.length || enriched.think_tanks_funded_count || 0,
          pacAssociationsCount: pacs.length || enriched.pac_associations_count || 0,
          conservativeOrgsCount: enriched.conservative_orgs_count || 0,
          liberalOrgsCount: enriched.liberal_orgs_count || 0,
        },

        // Financial
        netWorth: this._getNetWorth(enriched),

        // Political
        politicalScore: enriched.political_score || 0,
        politicalLeaning: leaning,

        // Relationships
        executiveCompanies,
        investmentCompanies,
        charities,
        thinkTanks,
        pacs,
        timelineEvents,

        // Associated company logos for header
        associatedLogos: [...executiveCompanies, ...investmentCompanies]
          .filter(c => c.logo)
          .slice(0, 4)
          .map(c => ({ url: c.logo, alt: c.logoAlt })),

        // External links
        socialLinks: [
          ...(enriched.website ? [{ icon: 'Globe', label: enriched.website.replace(/https?:\/\//, ''), url: enriched.website }] : []),
          ...(enriched._wikipediaUrl ? [{ icon: 'BookOpen', label: 'Wikipedia', url: enriched._wikipediaUrl }] : []),
        ],

        // Meta
        location: enriched._wikiLocation || enriched.location,
        education: enriched._wikiEducation || null,
        wikipediaUrl: enriched._wikipediaUrl || null,
        website: enriched.website,
        email: enriched.email,
        createdAt: enriched.created_at,
        updatedAt: enriched.updated_at,
      };
    } catch (error) {
      console.error('Error fetching full user profile:', error);
      throw error;
    }
  },

  /**
   * Generate contextual tags for an individual.
   */
  _generateTags(profile, executiveCompanies, investmentCompanies) {
    const tags = [];
    if (executiveCompanies.some(c => c.positionTitle?.toLowerCase().includes('founder'))) tags.push('Founder');
    if (executiveCompanies.some(c => c.positionTitle?.toLowerCase().includes('ceo'))) tags.push('CEO');
    if (executiveCompanies.some(c => c.positionTitle?.toLowerCase().includes('chairman'))) tags.push('Chairman');
    if (executiveCompanies.length > 0 && !tags.includes('Founder') && !tags.includes('CEO')) tags.push('Executive');
    if (investmentCompanies.length > 0) tags.push('Investor');
    if (profile.pac_associations_count > 0 || (profile.pac_relationships && profile.pac_relationships.length > 0)) tags.push('Political Donor');
    if (profile.charities_supported_count > 0 || (profile.organizational_associations && profile.organizational_associations.some(o => o.organization_type === 'charity'))) tags.push('Philanthropist');
    if (tags.length === 0) tags.push('Business Leader');
    return tags;
  },

  /**
   * Get all user profiles.
   */
  async getAllProfiles() {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(`
          *,
          organizational_associations(
            organization_name, organization_type, amount_donated, relationship_type
          ),
          pac_relationships(
            pac_name, political_affiliation, contribution_amount, relationship_type
          )
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(profile => this._transformToCardFormat(profile)) || [];
    } catch (error) {
      console.error('Error fetching user profiles:', error);
      return [];
    }
  },

  /**
   * Get current authenticated user profile.
   */
  async getCurrentUser() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      if (!data) return null;

      return {
        id: data.id,
        fullName: data.full_name,
        email: data.email,
        website: data.website,
        location: data.location,
        bio: data.bio,
        avatarUrl: data.avatar_url,
        role: data.role,
        isActive: data.is_active,
        companiesInvestedCount: data.companies_invested_count || 0,
        companiesOwnedCount: data.companies_owned_count || 0,
        charitiesSupportedCount: data.charities_supported_count || 0,
        thinkTanksFundedCount: data.think_tanks_funded_count || 0,
        pacAssociationsCount: data.pac_associations_count || 0,
        conservativeOrgsCount: data.conservative_orgs_count || 0,
        liberalOrgsCount: data.liberal_orgs_count || 0,
        politicalScore: data.political_score || 0,
        politicalLeaning: data.political_leaning,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  },

  /**
   * Update user profile.
   */
  async updateProfile(updates) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const dbUpdates = {};
      if (updates.fullName !== undefined) dbUpdates.full_name = updates.fullName;
      if (updates.bio !== undefined) dbUpdates.bio = updates.bio;
      if (updates.website !== undefined) dbUpdates.website = updates.website;
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;

      const { data, error } = await supabase
        .from('user_profiles')
        .update(dbUpdates)
        .eq('id', session.user.id)
        .select()
        .single();

      if (error) throw error;

      return {
        id: data.id,
        fullName: data.full_name,
        email: data.email,
        website: data.website,
        location: data.location,
        bio: data.bio,
        avatarUrl: data.avatar_url,
        role: data.role,
        isActive: data.is_active,
        companiesInvestedCount: data.companies_invested_count || 0,
        companiesOwnedCount: data.companies_owned_count || 0,
        charitiesSupportedCount: data.charities_supported_count || 0,
        thinkTanksFundedCount: data.think_tanks_funded_count || 0,
        pacAssociationsCount: data.pac_associations_count || 0,
        conservativeOrgsCount: data.conservative_orgs_count || 0,
        liberalOrgsCount: data.liberal_orgs_count || 0,
        politicalScore: data.political_score || 0,
        politicalLeaning: data.political_leaning,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  },
};
